import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface PaymentFailureBannerProps {
  /** Subscription status driving the banner */
  subscriptionStatus: 'past_due' | 'canceled';
  /** Grace period end date (ISO string). Used to compute countdown. */
  gracePeriodEndsAt?: string | null;
  /** Called when user dismisses the banner (session-level dismiss). */
  onDismiss?: () => void;
}

function computeDaysRemaining(endsAt: string | null | undefined): number | null {
  if (!endsAt) return null;
  const diffMs = new Date(endsAt).getTime() - Date.now();
  if (diffMs <= 0) return 0;
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function formatCountdown(days: number | null): string {
  if (days === null) return '';
  if (days === 0) return 'Today is the last day';
  if (days === 1) return '1 day';
  return `${days} days`;
}

const WarningIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
    className="flex-shrink-0 mt-0.5"
  >
    <path
      fillRule="evenodd"
      d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
      clipRule="evenodd"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M4 4l8 8M12 4l-8 8" />
  </svg>
);

const PaymentFailureBanner: React.FC<PaymentFailureBannerProps> = ({
  subscriptionStatus,
  gracePeriodEndsAt,
  onDismiss,
}) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(true);

  const daysRemaining = computeDaysRemaining(gracePeriodEndsAt);
  const isCanceled = subscriptionStatus === 'canceled';

  useEffect(() => {
    setVisible(true);
  }, [subscriptionStatus]);

  if (!visible) return null;

  const handleDismiss = () => {
    setVisible(false);
    onDismiss?.();
  };

  const handleUpdatePayment = () => {
    navigate('/billing/subscription');
  };

  if (isCanceled) {
    return (
      <div
        className="payment-failure-banner payment-failure-banner--canceled animate-banner-slide-down"
        role="alert"
        aria-live="assertive"
      >
        <div className="payment-failure-banner__inner">
          <div className="payment-failure-banner__content">
            <svg
              width="18"
              height="18"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
              className="flex-shrink-0 mt-0.5 text-red-400"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                clipRule="evenodd"
              />
            </svg>
            <span className="payment-failure-banner__message">
              <strong>Pro access ended.</strong> Resubscribe to restore your portfolio features.
            </span>
          </div>
          <div className="payment-failure-banner__actions">
            <button
              onClick={() => navigate('/billing/plans')}
              className="payment-failure-banner__cta payment-failure-banner__cta--danger"
            >
              Resubscribe →
            </button>
            <button
              onClick={handleDismiss}
              className="payment-failure-banner__dismiss"
              aria-label="Dismiss banner"
            >
              <CloseIcon />
            </button>
          </div>
        </div>
      </div>
    );
  }

  const countdownText = formatCountdown(daysRemaining);

  return (
    <div
      className="payment-failure-banner payment-failure-banner--past-due animate-banner-slide-down"
      role="alert"
      aria-live="polite"
    >
      <div className="payment-failure-banner__inner">
        <div className="payment-failure-banner__content">
          <WarningIcon />
          <span className="payment-failure-banner__message">
            <strong>Payment failed</strong>
            {countdownText ? (
              <>
                {' '}— Your paid access pauses in{' '}
                <strong className="payment-failure-banner__countdown">{countdownText}</strong>.
              </>
            ) : (
              ' — Your paid access may be paused soon.'
            )}
          </span>
        </div>
        <div className="payment-failure-banner__actions">
          <button
            onClick={handleUpdatePayment}
            className="payment-failure-banner__cta payment-failure-banner__cta--warning"
          >
            Update Payment Method →
          </button>
          <button
            onClick={handleDismiss}
            className="payment-failure-banner__dismiss"
            aria-label="Dismiss banner"
          >
            <CloseIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentFailureBanner;
